import React, { useEffect, useMemo } from 'react'
import { Text, useApplicationModals, ModalIds } from '@adc/ui-core'
import PaymentInstructionsContent, {
  PaymentInstructionsContentProps,
  formatDepositAmount,
} from './content'

export type PaymentInstructionsModalProps = Omit<
  PaymentInstructionsContentProps,
  'slots'
> & {
  open: boolean
  onClose: () => void
}

const MODAL_TITLE = 'A Deposit is Required to Bid'

const PaymentInstructionsModal = ({
  open,
  onClose,
  elmId = 'payment_instructions_modal',
  depositAmount,
  bidderId,
  wireDetails,
  checkDetails,
  onDownload,
  onEmail,
  contactEmail,
  contactPhone,
}: PaymentInstructionsModalProps) => {
  // Deps must stay identity-stable across renders: the owner-scoped
  // useApplicationModals hook auto-syncs modalProps while the modal is open,
  // so a new object identity on every render would cause an update loop.
  const modalProps = useMemo(
    () => ({
      elmId,
      size: 'small',
      title: MODAL_TITLE,
      onClose,
      children: (
        <>
          <Text className='u-mb-1' elmId='payment_instructions_modal_intro'>
            {`Submit your ${formatDepositAmount(
              depositAmount
            )} deposit no later than 2 business days before the auction starts using one of the methods below.`}
          </Text>
          <Text className='u-mb-4'>
            You will be able to bid once your deposit is received and verified.
          </Text>
          <PaymentInstructionsContent
            depositAmount={depositAmount}
            bidderId={bidderId}
            wireDetails={wireDetails}
            checkDetails={checkDetails}
            onDownload={onDownload}
            onEmail={onEmail}
            contactEmail={contactEmail}
            contactPhone={contactPhone}
            stickyFooter
          />
        </>
      ),
    }),
    [
      elmId,
      onClose,
      depositAmount,
      bidderId,
      wireDetails,
      checkDetails,
      onDownload,
      onEmail,
      contactEmail,
      contactPhone,
    ]
  )

  const modal = useApplicationModals(ModalIds.General, modalProps)

  useEffect(() => {
    if (open) {
      modal.open()
    } else {
      modal.close()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  return <div />
}

export default PaymentInstructionsModal
